// Shared CRUD table scaffold for multi-family pages (P1-10).
// Mirrors the look of ResourceList but adds a header action slot and a
// per-row action column. Pages drive their own modal + data fetching.
//
// Responsive: a real <table> on tablet/desktop, and a stacked card list on
// phones (label/value pairs) so nothing scrolls sideways on a small screen.
// We render one layout at a time (see useIsCompact) so the DOM isn't doubled.

import type { ReactNode } from 'react';
import { LoadingState } from './LoadingState.js';
import { useIsCompact } from '../lib/useIsCompact.js';

export interface Column<T> {
  key: string;
  header: string;
  width?: string;
  render?: (row: T) => ReactNode;
}

interface CrudTableProps<T> {
  rows: T[] | null;
  error: string | null;
  /**
   * GAP-P2-6 (audit 2026-05-21): widened from `string` to `ReactNode`
   * so pages can include inline `<Link>` CTAs in their empty states
   * (e.g. callbox: "Add a household first" with a link to /households).
   */
  emptyMessage: ReactNode;
  columns: Column<T>[];
  rowKey?: (row: T) => string;
  actions?: (row: T) => ReactNode;
  /** Screen-reader text shown while rows are loading — e.g. "Loading residents…". */
  loadingLabel?: string;
}

function cellValue<T>(column: Column<T>, row: T): ReactNode {
  return column.render
    ? column.render(row)
    : String((row as unknown as Record<string, unknown>)[column.key] ?? '');
}

export function CrudTable<T>({
  rows,
  error,
  emptyMessage,
  columns,
  rowKey,
  actions,
  loadingLabel,
}: CrudTableProps<T>) {
  const isCompact = useIsCompact();
  const keyFor = (row: T) =>
    rowKey?.(row) ?? (row as unknown as { id?: string }).id ?? JSON.stringify(row);
  const [primary, ...rest] = columns;

  return (
    <section className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden">
      {error ? (
        <div className="p-6 text-error font-body-md text-body-md" role="alert">
          {error}
        </div>
      ) : rows == null ? (
        <LoadingState label={loadingLabel} />
      ) : rows.length === 0 ? (
        <div className="p-12 text-center text-on-surface-variant font-body-md text-body-md">
          {emptyMessage}
        </div>
      ) : isCompact ? (
        <ul className="divide-y divide-outline-variant">
          {rows.map((row) => (
            <li key={keyFor(row)} className="space-y-3 p-4">
              {primary ? (
                <div className="font-title-sm text-title-sm text-on-surface">
                  {cellValue(primary, row)}
                </div>
              ) : null}
              {rest.length > 0 ? (
                <dl className="space-y-1 font-body-md text-body-md">
                  {rest.map((c) => (
                    <div key={c.key} className="flex justify-between gap-3">
                      <dt className="text-on-surface-variant">{c.header}</dt>
                      <dd className="min-w-0 break-words text-right text-on-surface">
                        {cellValue(c, row)}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              {actions ? <div className="flex flex-wrap gap-2 pt-1">{actions(row)}</div> : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-surface-container">
              <tr>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3"
                    style={c.width ? { width: c.width } : undefined}
                  >
                    {c.header}
                  </th>
                ))}
                {actions ? (
                  <th
                    className="text-right font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3"
                    style={{ width: '14%' }}
                  >
                    &nbsp;
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {rows.map((row) => (
                <tr key={keyFor(row)} className="hover:bg-surface-container-low transition-colors">
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 font-body-md text-body-md text-on-surface">
                      {cellValue(c, row)}
                    </td>
                  ))}
                  {actions ? <td className="px-4 py-3 text-right">{actions(row)}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export const fieldCls =
  'w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg font-body-md text-body-md focus:ring-2 focus:ring-primary outline-none';

export const selectCls = fieldCls;
